import type { ReactNode } from 'react';
import { ArrowRightIcon, ArrowsLeftRightIcon } from '@phosphor-icons/react';
import { SwapButton, TripTypeTabs } from '../../shared/route_controls.js';
import type { BusSearchValue } from '../models.js';
import { withBusDates } from './bus_search_rules.js';

/*
 * As peças da rota que a busca da CONVERSA acrescenta à barra (frame 01 do
 * "Conversacional → Bus"): as abas da forma da viagem e o inversor entre as
 * paradas — os mesmos controles da busca do aéreo.
 */

export type BusTripType = 'round_trip' | 'one_way';

/** Com a aba "Ida e volta" a volta é parte do pedido. */
export function missingReturn(tripType: BusTripType | undefined, value: BusSearchValue): boolean {
  return tripType === 'round_trip' && !value.returnDate;
}

/** As abas "Ida e volta / Somente ida" — só quando o host controla a forma da
 *  viagem e dá os rótulos. Somente ida não leva volta: a data da volta sai junto
 *  com a aba. */
export function BusTripTabs({
  tripType,
  labels,
  disabled,
  value,
  onChange,
  onTripTypeChange,
}: {
  readonly tripType: BusTripType | undefined;
  readonly labels: { readonly roundTrip: string; readonly oneWay: string } | undefined;
  readonly disabled: boolean;
  readonly value: BusSearchValue;
  readonly onChange: (value: BusSearchValue) => void;
  readonly onTripTypeChange: ((tripType: BusTripType) => void) | undefined;
}): ReactNode {
  if (!tripType || !labels || !onTripTypeChange) return null;
  return (
    <TripTypeTabs
      tabs={[
        { key: 'round_trip', label: labels.roundTrip, Icon: ArrowsLeftRightIcon },
        { key: 'one_way', label: labels.oneWay, Icon: ArrowRightIcon },
      ]}
      active={tripType}
      disabled={disabled}
      onSelect={(next) => {
        if (next === 'one_way' && value.returnDate) {
          onChange(withBusDates(value, value.outwardDate ? { from: value.outwardDate } : {}));
        }
        onTripTypeChange(next);
      }}
    />
  );
}

/** Origem ⇄ destino. Um lado vazio só muda de campo. */
function swapStops(value: BusSearchValue): BusSearchValue {
  const { from, to, ...rest } = value;
  return { ...rest, ...(to ? { from: to } : {}), ...(from ? { to: from } : {}) };
}

/** O inversor entre as duas rodoviárias — só com o rótulo. O invólucro é o que
 *  o esconde quando a fileira empilha (`bus_search_bar.css`). */
export function BusStopSwap({
  label,
  disabled,
  value,
  onChange,
}: {
  readonly label: string | undefined;
  readonly disabled: boolean;
  readonly value: BusSearchValue;
  readonly onChange: (value: BusSearchValue) => void;
}): ReactNode {
  if (!label) return null;
  return (
    <span className="iuk-bus-search-swap">
      <SwapButton
        label={label}
        disabled={disabled}
        onSwap={() => {
          onChange(swapStops(value));
        }}
      />
    </span>
  );
}
